// 3rd
// internal
// owned
import { assertLevel, value2Name, name2Value, Level } from '../log-level.js'
import Logger from '../logger.js'
// module vars

export default class Log4jsLogger extends Logger {
  /**
   * Creates a new ConsoleLogger instance.
   * @constructor
   * @param {string} name - The name of the logger.
   * @param {*} nativeLogger - Log4js library
   */
  constructor (name, nativeLogger) {
    super(name, nativeLogger)
    // @ts-ignore
    this._level = undefined // MUST NOT accept level from outside
    this._logger = nativeLogger
  }

  /**
   * 1. log4js may have many categories(loggerName) with diffirent level
   * 2. return the underlying log4js native logger's level
   * @returns {number} The current log level.
   */
  get level () {
    if (this._level == null) {
      const levelName = this._logger.level.levelStr
      if (levelName == null) {
        return Level.OFF
      }
      const level = name2Value(levelName)
      if (level == null) {
        throw new Error(`Unknown level name: ${levelName}`)
      }
      return level
    }
    return this._level
  }

  /**
   * set log4js native logger's level
   * @param {number} level
   * @override
   * @protectd
   */
  _setLevel (level) {
    assertLevel(level)
    const levelName = value2Name(level)
    if (levelName == null) {
      throw new Error(`Invalid log level: ${level}`)
    }
    // log4js use string level, eg. 'error, trace'
    this._logger.level = levelName.toLowerCase()
  }

  /**
   * Logs a fatal error message with timestamp and logger name.
   * Only outputs if fatal logging is enabled for this logger instance.
   * @param {...any} args - Arguments to log (will be space-separated)
   */
  _fatal (...args) {
    this._logger.fatal(...args)
  }

  /**
   * Logs an error message to the console with timestamp and logger name.
   * Only logs if error logging is enabled for this logger instance.
   * @param {...any} args - Arguments to be logged as error message
   */
  _error (...args) {
    this._logger.error(...args)
  }

  /**
   * Logs a warning message to the console if warn logging is enabled.
   * @param {...any} args - The arguments to log as a warning message.
   */
  _warn (...args) {
    this._logger.warn(...args)
  }

  /**
   * Logs debug messages to console if debug mode is enabled.
   * @param {...any} args - The data to be logged
   */
  _debug (...args) {
    this._logger.debug(...args)
  }

  /**
   * Logs an info message to the console with timestamp and logger name.
   * @param {...any} args - The data to be logged. Accepts multiple arguments.
   */
  _info (...args) {
    this._logger.info(...args)
  }

  /**
   * Logs a trace message with timestamp and logger name if trace logging is enabled.
   * @param {...any} args - Data to be logged as trace message.
   */
  _trace (...args) {
    this._logger.trace(...args)
  }
}
